import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import GoogleReviews from "@/components/GoogleReviews";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, ArrowRight, Check, Upload, ShirtIcon, Palette, Settings2, User, X } from "@/components/Icon";
import { useQuoteCart, sumQuoteItemSizes } from "@/contexts/QuoteCart";
import { Link } from "react-router-dom";

const PRODUCTS = [
  { id: "tshirt", label: "T-shirts" },
  { id: "hoodie", label: "Hoodies / Sweaters" },
  { id: "polo", label: "Polo shirts" },
  { id: "werkkleding", label: "Werkkleding" },
  { id: "sportkleding", label: "Sportkleding" },
  { id: "jassen", label: "Jassen / Bodywarmers" },
  { id: "caps", label: "Caps / Mutsen" },
  { id: "tassen", label: "Tassen" },
  { id: "kids", label: "Kids & Baby" },
  { id: "veiligheidshesjes", label: "Veiligheidshesjes" },
  { id: "accessoires", label: "Accessoires" },
  { id: "anders", label: "Anders" },
];

function guessProductType(name: string | null): string {
  if (!name) return "";
  const n = name.toLowerCase();
  if (n.includes("polo")) return "polo";
  if (n.includes("hood") || n.includes("sweat")) return "hoodie";
  if (n.includes("t-shirt") || n.includes("tee") || n.includes("tshirt")) return "tshirt";
  if (n.includes("jas") || n.includes("jacket") || n.includes("bodywarmer") || n.includes("softshell") || n.includes("parka") || n.includes("fleece")) return "jassen";
  if (n.includes("cap") || n.includes("muts") || n.includes("beanie") || n.includes("pet") || n.includes("hat")) return "caps";
  if (n.includes("tas") || n.includes("bag") || n.includes("backpack") || n.includes("rugzak")) return "tassen";
  if (n.includes("hesje") || n.includes("vest") || n.includes("hi-vis") || n.includes("warning")) return "veiligheidshesjes";
  if (n.includes("kid") || n.includes("baby") || n.includes("kinder")) return "kids";
  if (n.includes("work")) return "werkkleding";
  if (n.includes("sport") || n.includes("running") || n.includes("training")) return "sportkleding";
  return "anders";
}

const TECHNIQUES = [
  { id: "zeefdruk", label: "Zeefdruk", desc: "Ideaal voor grote oplages, scherpe kleuren" },
  { id: "borduren", label: "Borduren", desc: "Luxe uitstraling, duurzaam resultaat" },
  { id: "dtf", label: "DTF Transfer", desc: "Full-color, geschikt voor kleine oplages" },
  { id: "dtg", label: "DTG Printing", desc: "Direct op textiel, fotoprints mogelijk" },
  { id: "transferdruk", label: "Transferdruk / Flex", desc: "Teksten, rugnummers, felle kleuren" },
  { id: "advies", label: "Advies gewenst", desc: "Wij adviseren de beste techniek" },
];

const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "3XL"];

const STEPS = [
  { id: 1, label: "Product", icon: ShirtIcon },
  { id: 2, label: "Details", icon: Palette },
  { id: 3, label: "Techniek", icon: Settings2 },
  { id: 4, label: "Gegevens", icon: User },
];

const Configurator = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const prefillRef = searchParams.get("ref");
  const prefillProduct = searchParams.get("product");
  const prefillColor = searchParams.get("kleur");

  const { items: cartItems, removeItem, updateSizeQuantity, clear: clearCart } = useQuoteCart();
  const hasCart = cartItems.length > 0;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const initialRemarks = useMemo(() => {
    if (!prefillRef && !prefillProduct) return "";
    const parts = ["Gekozen product uit catalogus:"];
    if (prefillProduct) parts.push(`- ${prefillProduct}`);
    if (prefillRef) parts.push(`- Referentie: ${prefillRef}`);
    if (prefillColor) parts.push(`- Kleur: ${prefillColor}`);
    return parts.join("\n") + "\n\n";
  }, [prefillRef, prefillProduct, prefillColor]);

  const [form, setForm] = useState({
    product_type: guessProductType(prefillProduct),
    quantity: 10,
    sizes: {} as Record<string, number>,
    colors: prefillColor ?? "",
    technique: "",
    name: "",
    company: "",
    email: "",
    phone: "",
    deadline: "",
    remarks: initialRemarks,
  });

  // If prefill arrives after mount (rare), keep form in sync
  useEffect(() => {
    if (prefillRef || prefillProduct) {
      setForm((f) => ({
        ...f,
        product_type: f.product_type || guessProductType(prefillProduct),
        colors: f.colors || (prefillColor ?? ""),
        remarks: f.remarks || initialRemarks,
      }));
      // Skip product step if we already know the product
      if (guessProductType(prefillProduct)) setStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (key: string, value: any) => setForm((f) => ({ ...f, [key]: value }));

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const cartTotalQty = cartItems.reduce((s, i) => s + sumQuoteItemSizes(i.sizes), 0);

  const canProceed = () => {
    switch (step) {
      case 1: return hasCart || form.product_type !== "";
      case 2: return hasCart ? cartTotalQty > 0 : form.quantity >= 1;
      case 3: return true;
      case 4: return form.name && form.email && form.phone;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      let logo_url: string | null = null;

      if (logoFile) {
        const ext = logoFile.name.split(".").pop();
        const fileName = `quote-logos/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from("logos")
          .upload(fileName, logoFile, { contentType: logoFile.type });

        if (uploadError) throw uploadError;

        // Store only the storage path; the logos bucket is private and a
        // signed URL must be minted server-side when the file is needed.
        logo_url = fileName;
      }

      const cartLines = hasCart
        ? cartItems
            .map((i) => {
              const sizesStr = Object.entries(i.sizes).map(([k, v]) => `${k}:${v}`).join(", ");
              return `- ${i.brand ?? ""} ${i.name} (${i.ref})${i.color ? ` — ${i.color}` : ""} → ${sizesStr} (totaal ${sumQuoteItemSizes(i.sizes)})`;
            })
            .join("\n")
        : "";

      const combinedRemarks = [
        form.remarks.trim(),
        hasCart ? `\nProducten uit offerte-lijst:\n${cartLines}` : "",
      ]
        .filter(Boolean)
        .join("\n");

      const productTypeValue = hasCart
        ? cartItems.map((i) => `${i.brand ?? ""} ${i.name} (${i.ref})`).join("; ")
        : form.product_type;

      const aggregatedSizes = hasCart
        ? cartItems.reduce<Record<string, number>>((acc, i) => {
            for (const [k, v] of Object.entries(i.sizes)) acc[k] = (acc[k] ?? 0) + v;
            return acc;
          }, {})
        : form.sizes;

      const quantityValue = hasCart ? cartTotalQty : form.quantity;
      const colorsValue = hasCart
        ? cartItems.map((i) => i.color).filter(Boolean).join(", ")
        : form.colors.trim();

      const { error } = await supabase.from("quote_requests").insert({
        name: form.name.trim(),
        company: form.company.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim(),
        product_type: productTypeValue,
        technique: form.technique || null,
        quantity: quantityValue,
        sizes: Object.keys(aggregatedSizes).length > 0 ? aggregatedSizes : null,
        colors: colorsValue || null,
        logo_url,
        deadline: form.deadline || null,
        remarks: combinedRemarks || null,
      });

      if (error) throw error;

      try {
        await supabase.functions.invoke("send-form-notification", {
          body: {
            type: "offerte",
            data: {
              name: form.name.trim(),
              bedrijf: form.company.trim() || undefined,
              email: form.email.trim(),
              telefoon: form.phone.trim(),
              product: productTypeValue,
              techniek: form.technique || undefined,
              aantal: quantityValue,
              deadline: form.deadline || undefined,
              opmerkingen: combinedRemarks || undefined,
              items: hasCart ? cartItems : undefined,
              logo_path: logo_url || undefined,
            },
          },
        });
      } catch (err) {
        console.error("notification mail failed", err);
      }

      toast({
        title: "Offerte aanvraag verstuurd",
        description: "Wij nemen binnen 24 uur contact met u op.",
      });

      // Reset
      setStep(1);
      setForm({
        product_type: "", quantity: 10, sizes: {}, colors: "",
        technique: "", name: "", company: "", email: "",
        phone: "", deadline: "", remarks: "",
      });
      setLogoFile(null);
      setLogoPreview(null);
      if (hasCart) clearCart();
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Er ging iets mis",
        description: "Probeer het opnieuw of neem contact op via WhatsApp.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Gratis offerte aanvragen — Magic Print Den Haag</title>
        <meta name="description" content="Stel uw offerte samen voor bedrukte of geborduurde kleding. Antwoord binnen 24 uur van Magic Print Den Haag." />
        <link rel="canonical" href="https://www.magicprint.nl/offerte" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Magic Print" />
        <meta property="og:locale" content="nl_NL" />
        <meta property="og:title" content="Gratis offerte aanvragen — Magic Print Den Haag" />
        <meta property="og:description" content="Stel uw offerte samen voor bedrukte of geborduurde kleding. Antwoord binnen 24 uur." />
        <meta property="og:url" content="https://www.magicprint.nl/offerte" />
        <meta property="og:image" content="https://www.magicprint.nl/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Gratis offerte aanvragen — Magic Print Den Haag" />
        <meta name="twitter:description" content="Stel uw offerte samen voor bedrukte of geborduurde kleding. Antwoord binnen 24 uur." />
        <meta name="twitter:image" content="https://www.magicprint.nl/og-image.jpg" />
      </Helmet>
      <Navbar />
      <main className="pt-32 md:pt-40 pb-20 min-h-screen bg-background">

        <div className="container max-w-3xl">
          <h1 className="text-3xl md:text-5xl font-bold mb-2">
            Stel uw offerte samen
          </h1>
          <p className="text-muted-foreground mb-8 text-lg">
            Configureer uw product(en) en ontvang binnen 24 uur een offerte op maat.
          </p>

          {/* Step indicator */}
          <div className="flex items-center gap-1 mb-10">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center flex-1">
                <button
                  onClick={() => s.id < step && setStep(s.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all w-full
                    ${step === s.id ? "bg-primary text-primary-foreground" : ""}
                    ${step > s.id ? "bg-primary/20 text-primary cursor-pointer" : ""}
                    ${step < s.id ? "bg-muted text-muted-foreground" : ""}
                  `}
                >
                  <s.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{s.label}</span>
                </button>
                {i < STEPS.length - 1 && (
                  <div className={`h-0.5 w-4 flex-shrink-0 mx-1 ${step > s.id ? "bg-primary" : "bg-border"}`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Product keuze */}
          {step === 1 && (
            <div className="space-y-6 animate-fade-in">
              {hasCart ? (
                <>
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-[#0a2933]">Producten in uw offerte</h2>
                    <button
                      type="button"
                      onClick={clearCart}
                      className="text-xs text-muted-foreground hover:text-destructive"
                    >
                      Lijst leegmaken
                    </button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {cartItems.length} {cartItems.length === 1 ? "product" : "producten"} • totaal {cartTotalQty} stuks. U kunt verderop het aantal per maat aanpassen.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {cartItems.map((item) => (
                      <div
                        key={`${item.ref}-${item.color ?? ""}`}
                        className="flex gap-3 p-3 rounded-xl border-2 border-primary/30 bg-primary/5"
                      >
                        <div className="w-20 h-20 shrink-0 bg-white border border-border rounded-md overflow-hidden flex items-center justify-center">
                          {item.image ? (
                            <img src={item.image} alt={item.name} className="w-full h-full object-contain p-1" />
                          ) : (
                            <span className="text-[10px] text-muted-foreground">geen foto</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          {item.brand && (
                            <div className="text-[11px] text-muted-foreground truncate">{item.brand}</div>
                          )}
                          <div className="text-sm font-semibold truncate text-[#0a2933]">{item.name}</div>
                          <div className="text-[11px] text-muted-foreground">
                            {item.ref}{item.color ? ` • ${item.color}` : ""}
                          </div>
                          <div className="text-[11px] text-primary font-semibold mt-1">
                            {sumQuoteItemSizes(item.sizes)} stuks ({Object.entries(item.sizes).map(([k, v]) => `${k}:${v}`).join(", ")})
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(item.ref, item.color)}
                          aria-label="Verwijderen"
                          className="text-muted-foreground hover:text-destructive shrink-0 self-start"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="pt-2">
                    <Button asChild variant="outline" size="sm">
                      <Link to="/producten">+ Meer producten toevoegen</Link>
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-xl font-bold text-[#0a2933]">Kies uw product</h2>
                  <div className="text-sm text-muted-foreground -mt-3">
                    Of <Link to="/producten" className="text-primary font-semibold hover:underline">selecteer producten uit ons assortiment</Link> voor een nauwkeurige offerte.
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {PRODUCTS.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => update("product_type", p.id)}
                        className={`px-4 py-5 rounded-xl border-2 text-center transition-all hover:border-primary/50
                          ${form.product_type === p.id
                            ? "border-primary bg-primary/10 ring-2 ring-primary/20"
                            : "border-border bg-card"
                          }`}
                      >
                        <span className="text-sm font-medium text-[#0a2933]">{p.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 2: Details */}
          {step === 2 && (
            <div className="space-y-8 animate-fade-in">
              <h2 className="text-xl font-bold text-[#0a2933]">Product details</h2>

              {hasCart ? (
                <div className="space-y-3">
                  <Label>Aantal per maat</Label>
                  <p className="text-xs text-muted-foreground -mt-1">Pas indien nodig het aantal aan per product, kleur en maat.</p>
                  <div className="space-y-3">
                    {cartItems.map((item) => (
                      <div
                        key={`${item.ref}-${item.color ?? ""}`}
                        className="rounded-xl border border-border bg-card p-4"
                      >
                        <div className="flex gap-3 mb-3">
                          <div className="w-16 h-16 shrink-0 bg-white border border-border rounded-md overflow-hidden flex items-center justify-center">
                            {item.image && (
                              <img src={item.image} alt={item.name} className="w-full h-full object-contain p-1" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            {item.brand && (
                              <div className="text-[11px] text-muted-foreground truncate">{item.brand}</div>
                            )}
                            <div className="text-sm font-semibold truncate text-[#0a2933]">{item.name}</div>
                            <div className="text-[11px] text-muted-foreground">
                              {item.ref}{item.color ? ` • ${item.color}` : ""}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeItem(item.ref, item.color)}
                            aria-label="Verwijderen"
                            className="text-muted-foreground hover:text-destructive shrink-0 self-start"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(item.sizes).map(([size, qty]) => (
                            <div
                              key={size}
                              className="flex items-center gap-1.5 px-2 py-1 border border-border rounded bg-muted/30"
                            >
                              <span className="text-xs font-medium">{size}</span>
                              <Input
                                type="number"
                                min={0}
                                value={qty}
                                onChange={(e) =>
                                  updateSizeQuantity(
                                    item.ref,
                                    item.color,
                                    size,
                                    parseInt(e.target.value || "0", 10),
                                  )
                                }
                                className="w-16 h-7 px-1 text-xs text-center"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="text-sm font-semibold text-primary pt-2">
                    Totaal: {cartTotalQty} stuks
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    <Label>Aantal stuks *</Label>
                    <Input
                      type="number"
                      min={1}
                      value={form.quantity}
                      onChange={(e) => update("quantity", parseInt(e.target.value) || 0)}
                      className="max-w-[200px]"
                    />
                    <p className="text-xs text-muted-foreground">Minimaal 10 stuks voor zeefdruk, 20 voor borduren</p>
                  </div>

                  <div className="space-y-3">
                    <Label>Maatverdeling <span className="text-muted-foreground font-normal">(optioneel)</span></Label>
                    <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                      {SIZES.map((size) => (
                        <div key={size} className="space-y-1">
                          <label className="text-xs font-medium text-center block text-muted-foreground">{size}</label>
                          <Input
                            type="number"
                            min={0}
                            placeholder="0"
                            value={form.sizes[size] || ""}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              const newSizes = { ...form.sizes };
                              if (val > 0) newSizes[size] = val;
                              else delete newSizes[size];
                              update("sizes", newSizes);
                            }}
                            className="text-center h-10"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label>Gewenste kleuren <span className="text-muted-foreground font-normal">(optioneel)</span></Label>
                    <Input
                      value={form.colors}
                      onChange={(e) => update("colors", e.target.value)}
                      placeholder="Bijv. zwart, wit, marineblauw"
                    />
                  </div>
                </>
              )}

              <div className="space-y-3">
                <Label>Logo / Ontwerp uploaden <span className="text-muted-foreground font-normal">(optioneel)</span></Label>
                <div
                  className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors hover:border-primary/50
                    ${logoPreview ? "border-primary bg-primary/5" : "border-border"}`}
                  onClick={() => document.getElementById("logo-upload")?.click()}
                >
                  {logoPreview ? (
                    <div className="space-y-2">
                      <img src={logoPreview} alt="Logo preview" className="max-h-24 mx-auto rounded" />
                      <p className="text-sm text-muted-foreground">{logoFile?.name}</p>
                      <p className="text-xs text-primary">Klik om te wijzigen</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Klik om uw logo of ontwerp te uploaden
                      </p>
                      <p className="text-xs text-muted-foreground">PNG, JPG, PDF, AI of EPS</p>
                    </div>
                  )}
                  <input
                    id="logo-upload"
                    type="file"
                    className="hidden"
                    accept="image/*,.pdf,.ai,.eps"
                    onChange={handleLogoChange}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Techniek */}
          {step === 3 && (
            <div className="space-y-6 animate-fade-in">
              <h2 className="text-xl font-bold text-[#0a2933]">Kies een druktechniek</h2>
              <p className="text-muted-foreground">Weet u het niet zeker? Kies "Advies gewenst" en wij adviseren u.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {TECHNIQUES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => update("technique", t.id)}
                    className={`p-4 rounded-xl border-2 text-left transition-all hover:border-primary/50
                      ${form.technique === t.id
                        ? "border-primary bg-primary/10 ring-2 ring-primary/20"
                        : "border-border bg-card"
                      }`}
                  >
                    <span className="font-semibold block">{t.label}</span>
                    <span className="text-sm text-muted-foreground">{t.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Contact */}
          {step === 4 && (
            <div className="space-y-6 animate-fade-in">
              <h2 className="text-xl font-bold text-[#0a2933]">Uw gegevens</h2>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cfg-name">Naam *</Label>
                  <Input id="cfg-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Uw volledige naam" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cfg-company">Bedrijf / Organisatie</Label>
                  <Input id="cfg-company" value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Optioneel" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cfg-email">E-mail *</Label>
                  <Input id="cfg-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="uw@email.nl" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cfg-phone">Telefoon *</Label>
                  <Input id="cfg-phone" type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="06 - 12345678" required />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cfg-deadline">Gewenste leverdatum</Label>
                <Input id="cfg-deadline" type="date" value={form.deadline} onChange={(e) => update("deadline", e.target.value)} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cfg-remarks">Opmerkingen</Label>
                <Textarea
                  id="cfg-remarks"
                  rows={4}
                  value={form.remarks}
                  onChange={(e) => update("remarks", e.target.value)}
                  placeholder="Beschrijf uw wensen: logo-plaatsing, speciale eisen, etc."
                />
              </div>

              {/* Summary */}
              <div className="p-5 rounded-xl bg-card border border-border">
                <h3 className="font-semibold mb-3 text-[#0a2933]">Samenvatting</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-muted-foreground">Product:</span>
                  <span className="font-medium">{PRODUCTS.find((p) => p.id === form.product_type)?.label}</span>
                  <span className="text-muted-foreground">Aantal:</span>
                  <span className="font-medium">{form.quantity} stuks</span>
                  {form.technique && (
                    <>
                      <span className="text-muted-foreground">Techniek:</span>
                      <span className="font-medium">{TECHNIQUES.find((t) => t.id === form.technique)?.label}</span>
                    </>
                  )}
                  {form.colors && (
                    <>
                      <span className="text-muted-foreground">Kleuren:</span>
                      <span className="font-medium">{form.colors}</span>
                    </>
                  )}
                  {Object.keys(form.sizes).length > 0 && (
                    <>
                      <span className="text-muted-foreground">Maten:</span>
                      <span className="font-medium">
                        {Object.entries(form.sizes).map(([s, n]) => `${s}×${n}`).join(", ")}
                      </span>
                    </>
                  )}
                  {logoFile && (
                    <>
                      <span className="text-muted-foreground">Logo:</span>
                      <span className="font-medium">{logoFile.name}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex justify-between mt-10">
            <Button
              variant="outline"
              onClick={() => setStep((s) => s - 1)}
              disabled={step === 1}
            >
              <ArrowLeft className="mr-2 w-4 h-4" /> Vorige
            </Button>

            {step < 4 ? (
              <Button onClick={() => setStep((s) => s + 1)} disabled={!canProceed()}>
                Volgende <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading || !canProceed()}>
                {loading ? "Verzenden..." : (
                  <>Gratis offerte aanvragen <Check className="ml-2 w-4 h-4" /></>
                )}
              </Button>
            )}
          </div>
        </div>
      </main>
      <GoogleReviews />
      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default Configurator;
